import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {Column} from 'react-table'
import {Modal} from 'react-bootstrap'
import {
  KTable,
  KTableHeader,
  KTableSelectionCell,
  KTableLoading,
} from '../../../../../../components/UI'
import {useKTable} from '../../../../../../contexts/KTableProvider'
import {KTSVG} from '../../../../../../../../_metronic/helpers'
import {CREATION_CLEANING_ACTIVITIES_SERVICES} from '../../../../services/creation-cleaning-activities.service'
import {SCHEDULE_ACTIVITY_SERVICES} from '../../../../services/schedule-activity.service'
import {ShowSnackBar} from '../../../../../../util/functions'
import {ERROR_MESSAGE_SYSTEM} from '../../../../../../util/Constants'
import {useKhipu} from '../../../../../../hooks/useKhipu'
import {useParameters} from '../../../../hooks/central/useParameters'

interface Props {
  show: boolean
  setShow: Dispatch<SetStateAction<boolean>>
  reloadTable: () => void
  ServiceSchedulingId: string
  CampamentoId: string
  Asignados: string[]
}

export const ModalAddActivities: FC<Props> = ({
  show,
  setShow,
  reloadTable,
  ServiceSchedulingId,
  CampamentoId,
  Asignados,
}) => {
  const {
    data,
    setData,
    setPagination,
    setLoading,
    loading,
    pagination,
    selectedOn,
    clearSelected,
    selected,
    onSelect,
    updateTable,
    setUpdateTable,
    clearTable,
    setClearTable,
  } = useKTable()
  const {searchValues, clearSearchValues, changeSearchValues} = useKhipu()
  const [moduloOptions, setModuloOptions] = useState<{value: string; label: string}[]>([])
  const [moduloSelectedIds, setModuloSelectedIds] = useState<string[]>([])
  const searchValuesRef = useRef<any>(searchValues)
  const {typeActions, getTypeActions} = useParameters()

  useEffect(() => {
    searchValuesRef.current = searchValues
  }, [searchValues])

  useEffect(() => {
    getTypeActions(
      {pageNumber: 1, size: 200, tableName: 'TIPOACCION'},
      {},
      {idKey: 'id', labelKey: 'nombreCorto'},
    )
  }, [])

  const handleModuloMultiSelectChange = useCallback(
    (values: string[]) => {
      setModuloSelectedIds(values)
      const nextSearchValues: any = {...searchValuesRef.current}
      if (values.length > 0) {
        nextSearchValues.add_ModuloIds = values
      } else {
        delete nextSearchValues.add_ModuloIds
      }
      changeSearchValues(nextSearchValues)
    },
    [changeSearchValues],
  )

  // Selección por página: solo filas visibles y no asignadas
  const pageSelectableIds = useMemo(
    () => (data || []).filter((x: any) => !x.asignado).map((x: any) => x.Id),
    [data],
  )
  const isPageAllSelected = useMemo(() => {
    if (!pageSelectableIds.length) return false
    const setSel = new Set(selected.map((x: any) => String(x)))
    return pageSelectableIds.every((id: any) => setSel.has(String(id)))
  }, [pageSelectableIds, selected])

  const toggleSelectAllOnPage = () => {
    if (isPageAllSelected) {
      // deseleccionar solo los visibles seleccionados
      const setSel = new Set(selected.map((x: any) => String(x)))
      pageSelectableIds.forEach((id: any) => {
        if (setSel.has(String(id))) onSelect(id)
      })
    } else {
      // seleccionar los visibles no seleccionados
      const setSel = new Set(selected.map((x: any) => String(x)))
      pageSelectableIds.forEach((id: any) => {
        if (!setSel.has(String(id))) onSelect(id)
      })
    }
  }

  const filteredSelected = useMemo(() => {
    const asignadosSet = new Set(Asignados.map(String))

    return selected.filter((item) => {
      const idStr = String(item)
      const row = (data || []).find((x: any) => String(x?.Id ?? x?.id) === idStr)
      // Si la fila está en la página actual, confiar en `asignado` de la API (coherente con la UI).
      if (row) return !row.asignado
      return !asignadosSet.has(idStr)
    })
  }, [selected, Asignados, data])

  const columns: Column<any>[] = useMemo(
    () => [
      {
        Header: () => (
          <div className='form-check form-check-sm form-check-custom form-check-solid ms-6 me-4'>
            <input
              className='form-check-input'
              type='checkbox'
              checked={isPageAllSelected}
              onChange={toggleSelectAllOnPage}
            />
          </div>
        ),
        id: 'selection',
        Cell: ({...props}) => {
          const row = props.data[props.row.index]
          const rowId = row?.Id ?? row?.id
          return <KTableSelectionCell id={rowId} disable={row?.asignado} />
        },
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Asignado' />,
        accessor: 'asignado',
        Cell: ({...props}) => (
          <>
            <div className='d-flex justify-content-center flex-shrink-0'>
              {props.data[props.row.index].asignado ? 'SI' : 'NO'}
            </div>
          </>
        ),
      },
      {
        Header: (props) => (
          <KTableHeader
            tableProps={props}
            title='Actividad de Limpieza'
            name={'add_Actividad'}
            searcheable
          />
        ),
        accessor: 'Actividad',
      },
      {
        Header: (props) => (
          <KTableHeader
            tableProps={props}
            title='Tiempo'
            size='80px'
            name={'add_MinutosPromedio'}
            searcheable
            number
          />
        ),
        accessor: 'MinutosPromedio',
      },
      {
        Header: (props) => (
          <KTableHeader
            tableProps={props}
            title='Tipo de Acción'
            name={'add_TipoAccionId'}
            selecteable
            data={typeActions}
          />
        ),
        accessor: 'TipoAccion',
      },
      {
        Header: (props) => (
          <KTableHeader
            tableProps={props}
            title='Módulo'
            name={'add_ModuloIds'}
            multiSelecteable
            onMultiSelectValuesChange={handleModuloMultiSelectChange}
            data={moduloOptions}
          />
        ),
        accessor: 'Modulo',
      },
      {
        Header: (props) => (
          <KTableHeader
            tableProps={props}
            title='Habitación'
            name={'add_CodigoHabitacion'}
            searcheable
          />
        ),
        accessor: 'Habitacion',
      },
    ],
    [selectedOn, isPageAllSelected, selected, pageSelectableIds, moduloOptions, typeActions],
  )

  useEffect(() => {
    if (show === true) {
      clearSelected()
      clearSearchValues()
      setModuloSelectedIds([])
      getActivitiesByCampamentoList()
    } else {
      clearSelected() // Limpiar seleccionar al cerrar modal
      clearSearchValues()
      setModuloSelectedIds([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.perPage, show])

  useEffect(() => {
    if (!show) return
    const limpioModulos = clearTable
    if (clearTable) {
      setModuloSelectedIds([])
      const nextSearchValues: any = {...searchValuesRef.current}
      delete nextSearchValues.ModuloIds
      delete nextSearchValues.lstModulos
      changeSearchValues(nextSearchValues)
      searchValuesRef.current = nextSearchValues
      setClearTable(false)
    }
    if (updateTable) {
      // Tras Limpiar, moduloSelectedIds aún no se ha actualizado en este tick: forzar [] en el request
      getActivitiesByCampamentoList(1, limpioModulos ? [] : undefined)
      setUpdateTable(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTable, clearTable, show])

  const filterMap = {
    Actividad: 'Actividad',
    add_Actividad: 'Actividad',

    MinutosPromedio: 'MinutosPromedio',
    add_MinutosPromedio: 'MinutosPromedio',

    TipoAccionId: 'TipoAccionId',
    add_TipoAccionId: 'TipoAccionId',

    ModuloIds: 'ModuloIds',
    add_ModuloIds: 'ModuloIds',

    CodigoHabitacion: 'CodigoHabitacion',
    add_CodigoHabitacion: 'CodigoHabitacion',
  }

  const buildFilters = (searchValues: Record<string, any>) => {
    const result: Record<string, any> = {}

    Object.entries(searchValues).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return

      const apiKey = filterMap[key]
      if (apiKey) {
        result[apiKey] = value
      }
    })

    return result
  }

  const getActivitiesByCampamentoList = async (
    pageNumber = pagination.currentPage,
    overrideLstModulos?: string[],
  ) => {
    const modulosParaRequest =
      overrideLstModulos !== undefined ? overrideLstModulos : moduloSelectedIds
    try {
      setData([])
      setLoading(true)
      const filters = buildFilters(searchValues)
      const {data: response} = await CREATION_CLEANING_ACTIVITIES_SERVICES.getByServiceSchedulingId(
        {
          pageNumber,
          size: pagination.perPage,
          CampamentoId,
          ServiceSchedulingId,
          Disponibles: true,
          ...filters,
          ...(Array.isArray(modulosParaRequest) && modulosParaRequest.length > 0
            ? {lstModulos: modulosParaRequest}
            : {}),
        },
      )
      if (!response.error) {
        setData(response.data.Registros as any)
        const modulos = Array.isArray(response.data?.Modulos) ? response.data.Modulos : []
        const mappedModulos = modulos
          .filter((item: any) => item?.ModuloId && item?.Modulo)
          .map((item: any) => ({
            value: item.ModuloId,
            label: item.Modulo,
          }))
        setModuloOptions(mappedModulos)
        setPagination({
          ...pagination,
          currentPage: pageNumber,
          totalResults: response.total,
        })
      }
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setShow(false)
  }

  const onSubmit = async () => {
    if (filteredSelected.length === 0 && moduloSelectedIds.length === 0) {
      ShowSnackBar('Seleccione actividades o módulos para asignar', 'error')
      return
    }
    let assignSucceeded = false
    try {
      setLoading(true)
      const {data: response} = await SCHEDULE_ACTIVITY_SERVICES.assigningActivities({
        ServiceSchedulingId: [ServiceSchedulingId],
        ActividadConfiguracionId: filteredSelected.map((x) => x.toString()),
        ...(moduloSelectedIds.length > 0 ? {LstModulosId: moduloSelectedIds} : {}),
      })
      if (!response.error) {
        ShowSnackBar(response.message)
        assignSucceeded = true
      } else {
        ShowSnackBar(response.message ?? ERROR_MESSAGE_SYSTEM, 'error')
      }
    } catch (error) {
      ShowSnackBar(ERROR_MESSAGE_SYSTEM, 'error')
    } finally {
      setLoading(false)
      if (assignSucceeded) {
        clearSelected()
        clearSearchValues()
        setModuloSelectedIds([])
        searchValuesRef.current = {}
        setPagination((p) => ({...p, currentPage: 1}))
      }
      setShow(false)
      reloadTable()
    }
  }

  return (
    <Modal
      className='modal fade'
      show={show}
      dialogClassName='modal-xl'
      size='xl'
      aria-hidden='true'
      onHide={handleClose}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h5 className='modal-title'>Actividades</h5>
          <div className='btn btn-icon btn-sm btn-active-light-primary ms-2' onClick={handleClose}>
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
          </div>
        </div>
        <div className='modal-body'>
          <div className='position-relative' style={{minHeight: '300px'}}>
            <KTable
              data={data}
              columns={columns as any}
              showPageSizeSelector
              showButtonAdd={filteredSelected.length > 0 || moduloSelectedIds.length > 0}
              labelButtonAdd='Asignar'
              handleButtonAdd={onSubmit}
            />
            {loading ? <KTableLoading title='Cargando...' /> : null}
          </div>
        </div>
      </div>
    </Modal>
  )
}
